import { useDispatch, useSelector } from "react-redux";
import { useCallback, useMemo, useState } from "react";
import { stringify as queryStringify } from "query-string";
import { useLocation, useNavigate } from "react-router";
import { useMount, useWindowSize } from "react-use";
import { useTheme } from "styled-components";
import { cancelPendingAutoSave, useAddOrUpdateRemotePortfolio } from "../api";
import {
  BenchmarkAssetType,
  CompetenceType,
  DateRangeType,
  ReduxStateType,
  UserDataType,
  TabsType,
} from "./index";
import {
  addBenchmark,
  addNewPortfolio,
  RuleType,
  addOrUpdateAssetsToPortfolio,
  ReduxActions,
  removeAssetFromPortfolio,
  removeBenchmark,
  renameCurrentPortfolio,
  setCurrentPortfolio,
  setLanguage,
  setPeriod,
  setSpinner,
  balancePortfolio,
  addOrUpdateRemotePortfolioId,
  updateTotalAmount,
  setApplicationAmount,
  clonePortfolio,
  showSideEditor,
  updatePortfolioBeingEdited,
  showAssets,
  removePortfolio,
  setCurrency,
  updateFetchedDateRange,
  updateCustomDateRange,
  updateAssetLockInPortfolio,
  updateWindow,
  setConsolidated,
  setCompetence,
  showShareError,
  unlockAllPortfolioAssets,
  updateLoadingFromSharedLink,
  setStoreSharedLink,
  addCustomBenchmarkAsset,
  updateAvailableCompetences,
  updateShowAllSearchableCustomBenchmarks,
  updateIsUserAuthenticated,
  updateUserData,
  updateSuperCarteiraFeature,
  updateShowImportedNotifier,
  setBlankPortfolio,
  updateSelectedTab,
  setPortfolioEditorExpanded,
  setPortfolioEditorModalOpen,
  setPortfolioEditorSuperExpanded,
} from "./actions";
import { SearchResultsType } from "../components/shared";
import { AppDispatchType, CallableStateType } from "..";

import { setCredentials } from "../utils";

import { superCarteiraFeatures } from "../pages";
import { isDevMode } from "../utils/domain/EnvUtils";

export const useStoreForSharedLink = (): typeof hookReturn => {
  const dispatch = useDispatch();
  const storeCopy = useSelector((state: ReduxStateType) => state);

  const setStoreForSharedLink = (state: ReduxStateType): ReduxActions =>
    dispatch(setStoreSharedLink(state));

  const hookReturn = {
    storeCopy,
    setStoreForSharedLink,
  };

  return hookReturn;
};

export const useSpinner = (): [typeof spinner, typeof setSpinnerState] => {
  const dispatch = useDispatch();
  const spinner = useSelector((state: ReduxStateType) => state.spinner);

  const setSpinnerState = (spinnerState: boolean): ReduxActions =>
    dispatch(setSpinner(spinnerState));

  return [spinner, setSpinnerState];
};

export const useLanguage = (): [typeof language, typeof setLanguageState] => {
  const dispatch = useDispatch();
  const language = useSelector(
    (state: ReduxStateType) => state.globalConfigs.language
  );

  const setLanguageState = (lang: string): ReduxActions =>
    dispatch(setLanguage(lang));

  return [language, setLanguageState];
};

export const useCurrency = (): typeof hookReturn => {
  const dispatch = useDispatch();
  const currency = useSelector(
    (state: ReduxStateType) => state.globalConfigs.currency.currentCurrency
  );

  const availableCurrencies = useSelector(
    (state: ReduxStateType) => state.globalConfigs.currency.availableCurrencies
  );

  const setCurrencyState = (lang: string): ReduxActions =>
    dispatch(setCurrency(lang));

  const hookReturn = {
    currency,
    setCurrency: setCurrencyState,
    availableCurrencies,
  };

  return hookReturn;
};

export const useCurrentPortfolioId = (): [
  typeof currentPortfolioId,
  typeof setCurrentPortfolioId
] => {
  const dispatch = useDispatch();
  const currentPortfolioId = useSelector(
    (state: ReduxStateType) => state.currentPortfolioId
  );

  const setCurrentPortfolioId = (
    portfolioNumber: number | null
  ): ReduxActions => dispatch(setCurrentPortfolio(portfolioNumber));

  return [currentPortfolioId, setCurrentPortfolioId];
};

export const useCurrentPortfolio = (): typeof hookReturn => {
  const dispatch = useDispatch();
  const { send: addOrUpdateRemotePortfolio } = useAddOrUpdateRemotePortfolio();

  const currentPortfolioId = useSelector(
    (state: ReduxStateType) => state.currentPortfolioId
  );

  const selectedPortfolio = useSelector((state: ReduxStateType) =>
    currentPortfolioId === null
      ? null
      : {
          ...state.portfolios[currentPortfolioId],
        }
  );

  const updateRemotePortfolio = (): void => {
    dispatch(async (_: AppDispatchType, state: CallableStateType) => {
      const currentPortfolioContent =
        state().portfolios[currentPortfolioId || 0];
      const remotePortfolioId = await addOrUpdateRemotePortfolio(
        currentPortfolioContent
      );

      dispatch(
        addOrUpdateRemotePortfolioId(currentPortfolioId || 0, remotePortfolioId)
      );
    });
  };

  const addPortfolioAsset = (
    rule: RuleType,
    portfolioAssets: SearchResultsType,
    percentage: number | null,
    amount: number | null
  ): void => {
    dispatch(
      addOrUpdateAssetsToPortfolio(
        portfolioAssets,
        currentPortfolioId || 0,
        percentage,
        amount,
        rule,
        false
      )
    );
    updateRemotePortfolio();
  };

  const updatePortfolioAsset = (
    rule: RuleType,
    portfolioAssets: SearchResultsType,
    percentage: number | null,
    amount: number | null,
    isLocked: boolean
  ): void => {
    dispatch(
      addOrUpdateAssetsToPortfolio(
        portfolioAssets,
        currentPortfolioId || 0,
        percentage,
        amount,
        rule,
        isLocked
      )
    );
    updateRemotePortfolio();
  };

  const removePortfolioAsset = (assetId: string): void => {
    dispatch(removeAssetFromPortfolio(assetId, currentPortfolioId || 0));
    updateRemotePortfolio();
  };

  const updatePortfolioAssetLock = (assetId: string, locked: boolean): void => {
    dispatch(
      updateAssetLockInPortfolio(assetId, currentPortfolioId || 0, locked)
    );
    updateRemotePortfolio();
  };

  const portfolioAssets = useSelector(
    (state: ReduxStateType) => state.portfolios[currentPortfolioId || 0].assets
  );

  const portfolioName = useSelector(
    (state: ReduxStateType) => state.portfolios[currentPortfolioId || 0].name
  );

  const setPortfolioName = (newName: string): void => {
    dispatch(renameCurrentPortfolio(newName, currentPortfolioId || 0));
    updateRemotePortfolio();
  };

  const updatePortfolioTotal = (totalAmount: number): void => {
    dispatch(updateTotalAmount(totalAmount, currentPortfolioId || 0));
    updateRemotePortfolio();
  };

  const balanceCurrentPortfolio = (): void => {
    dispatch(balancePortfolio(currentPortfolioId || 0));
    updateRemotePortfolio();
  };

  const isLastPortfolio: boolean =
    Object.keys(useSelector((state: ReduxStateType) => state.portfolios))
      .length === 1;

  const isLastAssetInPortfolio: boolean = selectedPortfolio?.assets
    ? Object.keys(selectedPortfolio?.assets).length === 1
    : true;

  const isAnyAssetLocked: boolean = selectedPortfolio?.assets
    ? Object.keys(selectedPortfolio.assets).some(
        (asset) => selectedPortfolio.assets?.[asset].isLocked
      )
    : false;

  const removeCurrentPortfolio = (): void => {
    if (currentPortfolioId !== null)
      dispatch(removePortfolio(currentPortfolioId));
  };

  const unlockPortfolioAssets = (): void => {
    if (currentPortfolioId !== null) {
      dispatch(unlockAllPortfolioAssets(currentPortfolioId));
      updateRemotePortfolio();
    }
  };

  const hookReturn = {
    selectedPortfolio,
    portfolioAssets,
    addPortfolioAsset,
    updatePortfolioAsset,
    removePortfolioAsset,
    updatePortfolioAssetLock,
    portfolioName,
    setPortfolioName,
    updatePortfolioTotal,
    balanceCurrentPortfolio,
    isLastPortfolio,
    isLastAssetInPortfolio,
    removeCurrentPortfolio,
    updateRemotePortfolio,
    unlockPortfolioAssets,
    isAnyAssetLocked,
  };
  return hookReturn;
};

export const useAllPortfolios = (): typeof returnHook => {
  const dispatch = useDispatch();

  const allPortfolios = useSelector(
    (state: ReduxStateType) => state.portfolios
  );

  const allPortfoliosWithNamedKeys = useSelector((state: ReduxStateType) => {
    const arrayOfPortfolios = Object.keys(state.portfolios).map(
      (portfolioKey) => ({
        [`${String(
          state.portfolios[Number(portfolioKey)].assetType
        )}❤❤❤${String(state.portfolios[Number(portfolioKey)].identifier)}`]: {
          ...state.portfolios[Number(portfolioKey)],
        },
      })
    );

    return arrayOfPortfolios;
  });

  const createNewPortfolio = (): void => {
    dispatch(addNewPortfolio());
  };

  const returnHook = {
    allPortfolios,
    allPortfoliosWithNamedKeys,
    createNewPortfolio,
  };

  return returnHook;
};

export const usePeriod = (): typeof hookReturn => {
  const dispatch = useDispatch();
  const selectedPeriod = useSelector((state: ReduxStateType) => state.period);

  const setSelectedPeriod = (period: ReduxStateType["period"]): void => {
    dispatch(setPeriod(period));
  };

  const fetchedPeriodDate = useSelector(
    (state: ReduxStateType) => state.globalConfigs.fetchedDateRange
  );

  const setFetchedPeriodDate = (dateRange: DateRangeType): void => {
    dispatch(updateFetchedDateRange(dateRange));
  };

  const hookReturn = {
    selectedPeriod,
    setSelectedPeriod,
    fetchedPeriodDate,
    setFetchedPeriodDate,
  };

  return hookReturn;
};

export const useBenchmarks = (): typeof hookReturn => {
  const dispatch = useDispatch();
  const selectedBenchmarks = useSelector(
    (state: ReduxStateType) => state.benchmarks
  );

  const addSelectedBenchmark = (benchmarkItem: BenchmarkAssetType): void => {
    dispatch(addBenchmark(benchmarkItem));
  };

  const removeSelectedBenchmark = (benchmarkId: string): void => {
    dispatch(removeBenchmark(benchmarkId));
  };

  const hookReturn = {
    selectedBenchmarks,
    addSelectedBenchmark,
    removeSelectedBenchmark,
  };

  return hookReturn;
};

export const useMaxAllowedPortfolios = (): typeof hookReturn => {
  const allPortfolios = useSelector(
    (state: ReduxStateType) => state.portfolios
  );
  const maxAllowedPortfolios = useSelector(
    (state: ReduxStateType) => state.globalConfigs.maxAllowedPortfolios
  );

  const isPortfoliosMaxedOut = (): boolean =>
    Object.keys(allPortfolios).length >= maxAllowedPortfolios;

  const hookReturn = {
    maxAllowedPortfolios,
    isPortfoliosMaxedOut,
  };

  return hookReturn;
};

export const useInitialApplication = (): typeof hookReturn => {
  const dispatch = useDispatch();
  const initialApplication = useSelector(
    (state: ReduxStateType) => state.applicationAmount
  );

  const setInitialApplication = (amount: number): void => {
    dispatch(setApplicationAmount(amount));
  };

  const hookReturn = {
    initialApplication,
    setInitialApplication,
  };

  return hookReturn;
};

export const useClonePortfolio = (): typeof hookReturn => {
  const dispatch = useDispatch();
  const { send: addOrUpdateRemotePortfolio } = useAddOrUpdateRemotePortfolio();

  const allPortfolios = useSelector(
    (state: ReduxStateType) => state.portfolios
  );

  const clonePortfolioFromId = async (
    portfolioNumber: number
  ): Promise<void> => {
    const newPortfolioContent = {
      ...allPortfolios[portfolioNumber],
      identifier: "",
    };
    const remotePortfolioId = await addOrUpdateRemotePortfolio(
      newPortfolioContent
    );

    dispatch(clonePortfolio(portfolioNumber, remotePortfolioId));
  };

  const hookReturn = {
    clonePortfolioFromId,
  };

  return hookReturn;
};

export const useSideEditor = (): typeof hookReturn => {
  const dispatch = useDispatch();
  const isSideEditorActive = useSelector(
    (state: ReduxStateType) => state.globalConfigs.showSideEditor
  );

  const setSideEditorActive = (show: boolean): void => {
    dispatch(showSideEditor(show));
  };

  const setPortfolioBeingEdited = (id: number | null): void => {
    dispatch(updatePortfolioBeingEdited(id));
  };

  const portfolioBeingEdited = useSelector(
    (state: ReduxStateType) => state.globalConfigs.portfolioBeingEdited
  );

  const hookReturn = {
    isSideEditorActive,
    setSideEditorActive,
    portfolioBeingEdited,
    setPortfolioBeingEdited,
  };

  return hookReturn;
};

export const useShowAssets = (): typeof hookReturn => {
  const dispatch = useDispatch();
  const isAssetsShowing = useSelector(
    (state: ReduxStateType) => state.globalConfigs.showAssets
  );

  const setShowAssets = (show: boolean): void => {
    dispatch(showAssets(show));
  };

  const hookReturn = {
    isAssetsShowing,
    setShowAssets,
  };

  return hookReturn;
};

export const useCustomDateRange = (): typeof hookReturn => {
  const dispatch = useDispatch();
  const selectedCustomDateRange = useSelector(
    (state: ReduxStateType) => state.globalConfigs.customDateRange
  );

  const setCustomDateRange = (dateRange: DateRangeType): void => {
    dispatch(updateCustomDateRange(dateRange));
  };

  const hookReturn = {
    selectedCustomDateRange,
    setCustomDateRange,
  };

  return hookReturn;
};

export const useGlobalConfigs = (): typeof hookReturn => {
  const minDate = useSelector(
    (state: ReduxStateType) => state.globalConfigs.minSelectableDate
  );

  const windowValue = useSelector(
    (state: ReduxStateType) => state.globalConfigs.windowValue
  );

  const initialApplication = useSelector(
    (state: ReduxStateType) => state.globalConfigs.initialApplication
  );

  const defaultCustomBenchmarkAssetPercentage = useSelector(
    (state: ReduxStateType) =>
      state.globalConfigs.defaultCustomBenchmarkAssetPercentage
  );

  const minCharsForSearch = useSelector(
    (state: ReduxStateType) => state.globalConfigs.minCharsForSearch
  );

  const maxCustomBenchmarkAssetPercentage = useSelector(
    (state: ReduxStateType) =>
      state.globalConfigs.maxCustomBenchmarkAssetPercentage
  );

  const benchmarksInitialColor = useSelector(
    (state: ReduxStateType) => state.globalConfigs.benchmarksInitialColor
  );

  const orderedPortfolioCategories = useSelector(
    (state: ReduxStateType) => state.globalConfigs.orderedPortfolioCategories
  );

  const hookReturn = {
    windowValue,
    initialApplication,
    minDate,
    defaultCustomBenchmarkAssetPercentage,
    minCharsForSearch,
    maxCustomBenchmarkAssetPercentage,
    benchmarksInitialColor,
    orderedPortfolioCategories,
  };

  return hookReturn;
};

export const useSharedLink = (): typeof hookReturn => {
  const dispatch = useDispatch();
  const sharedLinkTasks = useSelector(
    (state: ReduxStateType) => state.sharedLinkTasks
  );

  const setLoadingPeriodsFromSharedLink = (loadingState: boolean): void => {
    dispatch(updateLoadingFromSharedLink(loadingState, "isLoadingPeriods"));
  };

  const setLoadingCompetenceFromSharedLink = (loadingState: boolean): void => {
    dispatch(updateLoadingFromSharedLink(loadingState, "isLoadingCompetence"));
  };

  const hookReturn = {
    sharedLinkTasks,
    setLoadingPeriodsFromSharedLink,
    setLoadingCompetenceFromSharedLink,
  };

  return hookReturn;
};

export const useWindowValue = (): typeof hookReturn => {
  const dispatch = useDispatch();
  const windowValue = useSelector((state: ReduxStateType) => state.windowValue);

  const setWindowValue = (windowNumber: number): void => {
    dispatch(updateWindow(windowNumber));
  };

  const hookReturn = {
    windowValue,
    setWindowValue,
  };

  return hookReturn;
};

export const useWallet = (): typeof hookReturn => {
  const dispatch = useDispatch();
  const selectedConsolidated = useSelector(
    (state: ReduxStateType) => state.consolidated
  );

  const availableConsolidated = useSelector(
    (state: ReduxStateType) => state.globalConfigs.availableConsolidated
  );

  const setSelectedConsolidated = (
    consolidatedBy: ReduxStateType["consolidated"]
  ): void => {
    dispatch(setConsolidated(consolidatedBy));
  };

  const selectedCompetence = useSelector(
    (state: ReduxStateType) => state.competence
  );

  const setSelectedCompetence = (
    competence: ReduxStateType["competence"]
  ): void => {
    dispatch(setCompetence(competence));
  };

  const availableCompetences = useSelector(
    (state: ReduxStateType) => state.globalConfigs.availableCompetences
  );

  const setAvailableCompetences = (competences: CompetenceType): void => {
    dispatch(updateAvailableCompetences(competences));
  };

  const hookReturn = {
    availableConsolidated,
    selectedConsolidated,
    setSelectedConsolidated,
    selectedCompetence,
    setSelectedCompetence,
    availableCompetences,
    setAvailableCompetences,
  };

  return hookReturn;
};

export const useShowShareError = (): typeof hookReturn => {
  const dispatch = useDispatch();
  const shareError = useSelector(
    (state: ReduxStateType) => state.globalConfigs.showShareError
  );

  const setShareError = (error: boolean): void => {
    dispatch(showShareError(error));
  };

  const hookReturn = {
    shareError,
    setShareError,
  };

  return hookReturn;
};

export const useShowImportedNotifier = (): typeof hookReturn => {
  const dispatch = useDispatch();
  const showImportedNotifier = useSelector(
    (state: ReduxStateType) => state.globalConfigs.showImportedNotifier
  );

  const setShowImportedNotifier = (error: boolean): void => {
    dispatch(updateShowImportedNotifier(error));
  };

  const hookReturn = {
    showImportedNotifier,
    setShowImportedNotifier,
  };

  return hookReturn;
};

export const useCustomBenchmarkAsset = (): typeof hookReturn => {
  const dispatch = useDispatch();
  const customBenchmarks = useSelector(
    (state: ReduxStateType) => state.searchableCustomBenchmarks
  );

  const addToCustomBenchmarkAssets = (
    customAssetItem: SearchResultsType
  ): void => {
    dispatch(addCustomBenchmarkAsset(customAssetItem));
  };

  const showAllSearchableCustomBenchmarks = useSelector(
    (state: ReduxStateType) => state.showAllSearchableCustomBenchmarks
  );

  const setShowAllSearchableCustomBenchmarks = (show: boolean): void => {
    dispatch(updateShowAllSearchableCustomBenchmarks(show));
  };

  const hookReturn = {
    customBenchmarks,
    addToCustomBenchmarkAssets,
    showAllSearchableCustomBenchmarks,
    setShowAllSearchableCustomBenchmarks,
  };

  return hookReturn;
};

export const useSelectedTab = (): typeof hookReturn => {
  const dispatch = useDispatch();

  const selectedTab = useSelector((state: ReduxStateType) => state.selectedTab);

  const setSelectedTab = useCallback(
    (data: TabsType): void => {
      dispatch(updateSelectedTab(data));
    },
    [dispatch]
  );

  const hookReturn = {
    selectedTab,
    setSelectedTab,
  };

  return hookReturn;
};

export const useAuth = (): typeof hookReturn => {
  const dispatch = useDispatch();

  const isUserAuthenticated = useSelector(
    (state: ReduxStateType) => state.isUserAuthenticated
  );

  const setIsUserAuthenticated = useCallback(
    (auth: boolean): void => {
      dispatch(updateIsUserAuthenticated(auth));
    },
    [dispatch]
  );

  const setLocalStorageToken = (token: string, login: string): void =>
    setCredentials(token, login);

  const hookReturn = {
    isUserAuthenticated,
    setIsUserAuthenticated,
    setLocalStorageToken,
  };

  return hookReturn;
};

export const useUserData = (): typeof hookReturn => {
  const dispatch = useDispatch();

  const userData = useSelector((state: ReduxStateType) => state.userData);

  const setUserData = (data: UserDataType): void => {
    dispatch(updateUserData(data));
  };

  const hookReturn = {
    userData,
    setUserData,
  };

  return hookReturn;
};

export const useSuperCarteiraFeature = (): typeof hookReturn => {
  const dispatch = useDispatch();

  const superCarteiraFeature = useSelector(
    (state: ReduxStateType) => state.globalConfigs.superCarteiraFeature
  );

  const setSuperCarteiraFeature = useCallback(
    (data: superCarteiraFeatures): void => {
      dispatch(updateSuperCarteiraFeature(data));
    },
    [dispatch]
  );

  const hookReturn = {
    superCarteiraFeature,
    setSuperCarteiraFeature,
  };

  return hookReturn;
};

export const useBlankPortfolio = (): typeof hookReturn => {
  const dispatch = useDispatch();

  const resetToBlankPortfolio = useCallback((): void => {
    dispatch(setBlankPortfolio());
  }, [dispatch]);

  const hookReturn = {
    resetToBlankPortfolio,
  };

  return hookReturn;
};

export const useSuperCarteiraRedirections = (): typeof hookReturn => {
  const { userData } = useUserData();
  const { setSuperCarteiraFeature } = useSuperCarteiraFeature();
  const { resetToBlankPortfolio } = useBlankPortfolio();
  const navigate = useNavigate();

  const gotoFeatureWithUserData = useCallback(
    (feature: superCarteiraFeatures, dataFromUser: UserDataType) => {
      // Cancel any queued auto save. Without this, a save built with
      // the previous feature's snapshot could fire after the switch
      // and contaminate the new feature's slot.
      cancelPendingAutoSave();

      const {
        id,
        benchmark,
        period,
        windowValue,
        initialApplication,
        currentPortfolioId,
        selectedCustomDateRange,
        consolidated,
        competence,
      } = dataFromUser.portfolios[feature];

      const isFeatureDataValid = id !== null;

      // Set the feature BEFORE resetting/navigating. Any save fired
      // by the cascading useEffects will read the new feature, not
      // the old one.
      setSuperCarteiraFeature(feature);

      if (isFeatureDataValid) {
        const queryString = queryStringify(
          {
            portfolios: id,
            benchmarks: benchmark,
            period,
            windowValue,
            initialApplication,
            currentPortfolioId,
            selectedCustomDateRange,
            consolidated,
            competence,
          },
          { arrayFormat: "separator", arrayFormatSeparator: "," }
        );

        const sharedLink = `/shared?${queryString}`;

        resetToBlankPortfolio();
        navigate(sharedLink);
        return;
      }

      resetToBlankPortfolio();
      navigate("/");
    },
    [setSuperCarteiraFeature, navigate, resetToBlankPortfolio]
  );

  const gotoFeature = useCallback(
    (feature: superCarteiraFeatures): void => {
      gotoFeatureWithUserData(feature, userData);
    },
    [userData, gotoFeatureWithUserData]
  );

  const hookReturn = {
    gotoFeatureWithUserData,
    gotoFeature,
  };

  return hookReturn;
};

export const useIsSmallSize = (): typeof hookReturn => {
  const { width } = useWindowSize();
  const isSmallScreen = width < 1200;

  const hookReturn = {
    isSmallScreen,
  };

  return hookReturn;
};

export const usePortfolioEditor = (): typeof hookReturn => {
  const dispatch = useDispatch();
  const theme = useTheme();
  const location = useLocation();

  const isOnHomePage = location.pathname === "/";
  const hasInitialExpandedState = theme.portfolioEditor.initialExpandedState;
  const allowModalEditing =
    theme.portfolioEditor.allowModalEditingOnAnalysisPage;

  const reduxIsExpanded = useSelector(
    (state: ReduxStateType) => state.globalConfigs.portfolioEditor.isExpanded
  );

  const isSuperExpanded = useSelector(
    (state: ReduxStateType) =>
      state.globalConfigs.portfolioEditor.isSuperExpanded
  );

  const setIsSuperExpanded = useCallback(
    (state: boolean) => {
      dispatch(setPortfolioEditorSuperExpanded(state));
    },
    [dispatch]
  );

  const toggleIsSuperExpanded = useCallback(() => {
    dispatch(setPortfolioEditorSuperExpanded(!isSuperExpanded));
  }, [dispatch, isSuperExpanded]);

  const isExpanded =
    (isOnHomePage && hasInitialExpandedState) || reduxIsExpanded;

  const isModalOpen = useSelector(
    (state: ReduxStateType) => state.globalConfigs.portfolioEditor.isModalOpen
  );

  const toggleExpandedPortfolio = useCallback(() => {
    dispatch(setPortfolioEditorExpanded(!isExpanded));
  }, [dispatch, isExpanded]);

  const openModal = useCallback(() => {
    dispatch(setPortfolioEditorModalOpen(true));
    dispatch(setPortfolioEditorExpanded(true));
  }, [dispatch]);

  const closeModal = useCallback(
    (onlyUnsetSuperExpanded = false) => {
      if (onlyUnsetSuperExpanded) {
        setIsSuperExpanded(false);
        return;
      }

      dispatch(setPortfolioEditorModalOpen(false));
      dispatch(setPortfolioEditorExpanded(false));
      setIsSuperExpanded(false);
    },
    [dispatch, setIsSuperExpanded]
  );

  const hookReturn = {
    isExpanded,
    toggleExpandedPortfolio,
    isModalOpen,
    openModal,
    closeModal,
    allowModalEditing,
    isSuperExpanded,
    setIsSuperExpanded,
    toggleIsSuperExpanded,
  };

  return hookReturn;
};

export const useMobileWarning = (): typeof hookReturn => {
  const theme = useTheme();
  const [showMobileWarning, setShowMobileWarning] = useState(false);
  const { width } = useWindowSize();
  const encodedKey = useMemo(() => btoa("mobileCurrentTime"), []);

  const msDatePlusMinutes = (msDate?: number, minutes = 0): number => {
    const baseMs = msDate !== undefined ? msDate : Date.now();
    return baseMs + minutes * 60 * 1000;
  };

  const setMobileLastOkTime = useCallback((): void => {
    try {
      const encodedCurrentTime = btoa(Date.now().toString());

      localStorage.setItem(encodedKey, encodedCurrentTime);
    } catch (error) {
      if (isDevMode())
        console.error("Error encoding or setting mobileCurrentTime:", error);
    }
  }, [encodedKey]);

  const getMobileLastOkTime = (): number | null => {
    try {
      const encodedStoredLastOkTime = localStorage.getItem(encodedKey);
      const storedMobileLastOkTime = encodedStoredLastOkTime
        ? Number(atob(encodedStoredLastOkTime))
        : null;

      return storedMobileLastOkTime ?? 0;
    } catch (error) {
      if (isDevMode())
        console.error("Error decoding mobileCurrentTime:", error);

      return 0;
    }
  };

  useMount(() => {
    const isMobile = width < theme.mobileWarning.maxWidthToTrigger;

    const isLastWarningDurationExpired =
      Date.now() >
      msDatePlusMinutes(
        getMobileLastOkTime() ?? 0,
        theme.mobileWarning.minutesBetweenWarnings
      );

    if (isDevMode()) {
      console.log(
        ">>> Mobile Warning Timer! Expiring in (ms):",
        Date.now() -
          msDatePlusMinutes(
            getMobileLastOkTime() ?? 0,
            theme.mobileWarning.minutesBetweenWarnings
          )
      );
    }

    if (
      isMobile &&
      isLastWarningDurationExpired &&
      theme.mobileWarning.enabled
    ) {
      setShowMobileWarning(true);
    }
  });

  const resetWarningTimer = useCallback(() => {
    setShowMobileWarning(false);
    setMobileLastOkTime();
  }, [setShowMobileWarning, setMobileLastOkTime]);

  const hookReturn = {
    setMobileLastOkTime,
    getMobileLastOkTime,
    showMobileWarning,
    setShowMobileWarning,
    resetWarningTimer,
  };

  return hookReturn;
};
